import _ from 'underscore';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { Select } from '@bingads-webui-react/primitive';
import { ValidationGroup } from '@bingads-webui-react/primitive-validation';
import { PromotionAdExtension } from '@bingads-webui-campaign/campaign-ui-schemas';
import { InputWithExtra } from '@bingads-webui-campaign/ad-extensions-shared';
import {
  promotionDetailTypesMap, isPromotionCode, isOnOrdersOver, getPromotionDetailTypeForModel,
  cleanPromotionDetailTypeData,
} from '../rules/promotion-detail';

const promotionDetailTypes = {
  [promotionDetailTypesMap.None]: _TL_('None'),
  [promotionDetailTypesMap.OnOrdersOver]: _TL_('On orders over'),
  [promotionDetailTypesMap.PromotionCode]: _TL_('Promotion code'),
};

function PromotionDetailSelect({
  className, id, onChange, value: selected, i18n,
}) {
  const [selectedPromotionDetailType, setSelectedPromotionDetailType] = useState(selected);

  return (
    <Select
      id={id}
      className={className}
      value={selectedPromotionDetailType}
      onChange={(event) => {
        const { value } = event.target;
        setSelectedPromotionDetailType(value);
        onChange(value);
      }}
    >
      {_.pairs(promotionDetailTypes).map(([key, value]) =>
        (
          <option key={key} value={key}>
            {i18n.getString(value)}
          </option>
        ))}
    </Select>
  );
}

PromotionDetailSelect.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};

PromotionDetailSelect.defaultProps = {
  id: undefined,
  value: 'None',
};

const SubSchema = cloneDeep({
  properties: {
    ..._.pick(PromotionAdExtension.properties, 'PromotionCode', 'OrdersOverAmount'),
  },
});

const Schema = (promotionDetailType) => {
  const schema = cloneDeep(SubSchema);

  schema.properties.PromotionCode.required = isPromotionCode(promotionDetailType);
  schema.properties.OrdersOverAmount.required = isOnOrdersOver(promotionDetailType);

  return schema;
};

export function PromotionDetail({
  value, onChange, i18n, legacyI18n, id, promotionDetailTypeSelectClassName,
  inputContainerClass, inputClass, inputExtraClass,
}) {
  const [promotionDetailType, setPromotionDetailType]
    = useState(getPromotionDetailTypeForModel(value));
  const [data, setData] =
    useState({ ...cleanPromotionDetailTypeData(promotionDetailType, value), promotionDetailType });
  const [schema, setSchema] = useState(Schema(promotionDetailType));

  const onPromotionDetailTypeChange = (newPromotionDetailType) => {
    setData((currentData) => {
      const mergedData = {
        ...currentData,
        ...cleanPromotionDetailTypeData(newPromotionDetailType, currentData),
        // Injecting UI state to help interconnected components, e.g. Currency
        promotionDetailType: newPromotionDetailType,
      };
      setPromotionDetailType(newPromotionDetailType);
      setSchema(Schema(newPromotionDetailType));
      onChange(mergedData);
      return mergedData;
    });
  };

  const onValidate = ({ data: newData }) => {
    setData((currentData) => {
      const mergedData = { ...currentData, ...newData };
      onChange(mergedData);
      return mergedData;
    });
  };

  return (
    <React.Fragment>
      <PromotionDetailSelect
        className={classnames(promotionDetailTypeSelectClassName, 'promotion-extension-editor__promotion-detail')}
        id={id}
        value={promotionDetailType}
        onChange={onPromotionDetailTypeChange}
        i18n={i18n}
      />
      <ValidationGroup
        validationProps={{
          schema,
          i18n: legacyI18n,
          data,
          onValidate,
        }}
      >
        {isPromotionCode(promotionDetailType) &&
          <InputWithExtra
            containerClass={inputContainerClass}
            className={inputClass}
            id="promotion-code"
            validationProps={{
              field: 'PromotionCode',
            }}
            extra={() => { }}
          />
        }
        {isOnOrdersOver(promotionDetailType) &&
          <InputWithExtra
            containerClass={inputContainerClass}
            className={inputClass}
            id="order-over-amount"
            validationProps={{
              field: 'OrdersOverAmount',
            }}
            extra={() => <span className={inputExtraClass}>{value.CurrencyCode}</span>}
          />
        }
      </ValidationGroup>
    </React.Fragment>
  );
}

PromotionDetail.propTypes = {
  id: PropTypes.string.isRequired,
  promotionDetailTypeSelectClassName: PropTypes.string.isRequired,
  inputContainerClass: PropTypes.string.isRequired,
  inputClass: PropTypes.string.isRequired,
  inputExtraClass: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.shape({}),
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  legacyI18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};

PromotionDetail.defaultProps = {
  value: undefined,
};
